/**
 * Each camera's live object, which the media_server reports through the relay: capabilities and
 * stream profiles included, which the cloud's row lacks.
 *
 * A member is keyed by the camera and its server, because a loader gets only the abort signal.
 * A `devices` change frame reloads the members of the camera it names.
 *
 * Two sources fill a member. A load reads the media_server. The motion editor writes
 * `enabledAgents` to the cloud row, and the media_server takes that write only some time later,
 * so a load could answer the old list. Thus, `answerEnabledAgentsAtom` lays the written list
 * over the live object, and no request is sent.
 */

import { atom, type WritableAtom } from 'jotai';
import { selectAtom } from 'jotai/utils';
import { atomFamily } from 'jotai-family';

import { makeKeyedFamily } from '../keyedFamily';
import { snapshotAtoms, snapshotMemberAtom } from '../snapshotAtoms';
import { api } from '@/api';
import type {
    EditedCameras, FlatDevice, LiveCameraKey, LiveCameraSnapshot,
} from '@/types/devices';
import { type StreamInput, StreamInputKind } from '@/types/notifications';
import { PortalEvent } from '@/types/portal';
import { SnapshotCommand } from '@/types/snapshot';

import { cameraAtom, devicesAtom } from './devicesAtoms';

/** One change of a camera often comes as a burst of frames: one load answers all of them. */
export const LIVE_CAMERA_RELOAD_AFTER_MS = 300;

const PENDING: LiveCameraSnapshot = { camera: null, isLoading: true, error: null };

/** One camera's live object: read it for the snapshot, set it with a command. */
export const liveCameraAtom = makeKeyedFamily(
    ({ cameraId, serverId }: LiveCameraKey) => snapshotMemberAtom(
        snapshotAtoms<LiveCameraSnapshot>(
            PENDING,
            async (): Promise<LiveCameraSnapshot> => ({
                camera: { ...await api.getDeviceLive(cameraId, { serverId }), serverId },
                isLoading: false,
                error: null,
            }),
            { reloadAfterMs: LIVE_CAMERA_RELOAD_AFTER_MS },
        ),
    ),
);

const RELOAD = { command: SnapshotCommand.Reload } as const;

interface EditedCamera {
    camera: FlatDevice;
    /** False until the member has a live object or an error. */
    hasAnswered: boolean;
}

/** One camera as the panel edits it: the live object, else the roster row. */
const editedCameraAtom = atomFamily((id: string) =>
    atom((get): EditedCamera | undefined => {
        const row = get(cameraAtom(id));
        if (row === undefined) {
            return undefined;
        }
        // A camera with no server has no media_server to read the live object from.
        if (row.serverId === '') {
            return { camera: row, hasAnswered: true };
        }
        const { camera, error } = get(liveCameraAtom({ cameraId: id, serverId: row.serverId }));
        return { camera: camera ?? row, hasAnswered: camera !== null || error !== null };
    }));

const NO_CAMERAS: EditedCameras = { cameras: [], isLoading: false };
const LOADING_CAMERAS: EditedCameras = { cameras: [], isLoading: true };

function areSameCameras(left: EditedCameras, right: EditedCameras): boolean {
    return left.isLoading === right.isLoading &&
        left.cameras.length === right.cameras.length &&
        left.cameras.every((camera, index) => camera === right.cameras[index]);
}

/**
 * The cameras of a selection, keyed by `getIdsKey`, in the sorted id order. A camera with no
 * roster row is dropped. The array keeps its identity while each camera keeps its identity,
 * because the form baseline compares by identity.
 */
export const editedCamerasAtom = atomFamily((idsKey: string) => {
    const ids = idsKey === '' ? [] : idsKey.split(',');
    const editedAtom = atom((get): EditedCameras => {
        if (ids.length === 0) {
            return NO_CAMERAS;
        }
        const editedCameras = ids
            .map((id) => get(editedCameraAtom(id)))
            .filter((entry) => entry !== undefined);
        if (!get(devicesAtom).isLoaded || editedCameras.some((entry) => !entry.hasAnswered)) {
            return LOADING_CAMERAS;
        }
        return { cameras: editedCameras.map((entry) => entry.camera), isLoading: false };
    });
    return selectAtom(editedAtom, (editedCameras): EditedCameras => editedCameras, areSameCameras);
});

function getMembersOf(cameraId: string): LiveCameraKey[] {
    return [...liveCameraAtom.getParams()].filter((key) => key.cameraId === cameraId);
}

/** Loads each member of the camera again, after the delay of the family. */
export const reloadLiveCameraAtom: WritableAtom<null, [cameraId: string], void> = atom(
    null,
    (_get, set, cameraId: string): void => {
        for (const key of getMembersOf(cameraId)) {
            set(liveCameraAtom(key), RELOAD);
        }
    },
);

/** Puts a camera that a write returned on screen, with no request. */
export const answerLiveCameraAtom: WritableAtom<
    null, [key: LiveCameraKey, camera: FlatDevice], void
> = atom(null, (_get, set, key: LiveCameraKey, camera: FlatDevice): void => {
    set(liveCameraAtom(key), {
        command: SnapshotCommand.Answer,
        snapshot: { camera, isLoading: false, error: null },
    });
});

/** Lays the list that the editor wrote over the live object of each member of the camera. */
export const answerEnabledAgentsAtom: WritableAtom<
    null, [cameraId: string, enabledAgents: string[]], void
> = atom(null, (get, set, cameraId: string, enabledAgents: string[]): void => {
    for (const key of getMembersOf(cameraId)) {
        const { camera } = get(liveCameraAtom(key));
        if (camera === null) {
            set(liveCameraAtom(key), RELOAD);
        } else {
            set(liveCameraAtom(key), {
                command: SnapshotCommand.Answer,
                snapshot: {
                    camera: { ...camera, enabledAgents }, isLoading: false, error: null,
                },
            });
        }
    }
});

/** What the notifications stream means for the live cameras. */
export const liveCamerasStreamAtom = atom(null, (_get, set, input: StreamInput): void => {
    // The gap may have hidden a change frame.
    if (input.kind === StreamInputKind.Reopened) {
        for (const key of liveCameraAtom.getParams()) {
            set(liveCameraAtom(key), RELOAD);
        }
        return;
    }
    if (input.kind !== StreamInputKind.Frame) {
        return;
    }
    const { frame } = input;
    if (frame.type === 'change' && frame.resource === 'devices') {
        set(reloadLiveCameraAtom, frame.id);
    }
});

/** A load that failed while the cloud was away stays failed until a reload. */
export const liveCamerasPortalAtom = atom(null, (_get, set, event: PortalEvent): void => {
    if (event !== PortalEvent.Answered) {
        return;
    }
    for (const key of liveCameraAtom.getParams()) {
        set(liveCameraAtom(key), RELOAD);
    }
});
